import { AppConfigContext } from '@App/AppConfigContext';
import { access_to_medication } from '@App/data/benefits.json';
import providerData from '@App/data/provider.zava.json';
import { VisualizationBoxItems } from '@App/data/VisualizationBoxItems';
import { ensureHash } from '@App/helper/ensureHash';
import { usePrescriptionStore } from '@App/stores';
import { Features } from '@Config/Features';
import { Area } from '@Domain/model/area';
import { InsuranceType } from '@Framework/constants/InsuranceType';
import { useFeature } from '@Framework/hooks/useFeature';
import { useInsuranceTypeParam } from '@Framework/hooks/useInsuranceTypeParam';
import { useTreatments } from '@Framework/hooks/useTreatments';
import { Box, Typography } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { PrimaryAlert } from 'src/components/molecules/PrimaryAlert';

import { Button } from '../../../atoms/Button/Button';
import Feature from '../../../atoms/Feature';
import { Modal } from '../../../atoms/Modal/Modal';
import { OdsLayout } from '../../../layouts/odsLayout';
import { VisualizationBox } from '../../../molecules/VisualizationBox/VisualizationBox';
import { BasicSelect } from '../../../organisms/BasicSelect';
import { ListBox } from '../../../organisms/ListBox/ListBox';
import { ProviderTile } from '../../../organisms/ProviderTile/ProviderTile';
import { TransitionModal } from '../../../organisms/TransitionModal/TransitionModal';

/**
 * Provider data excluding the benefits for the purpose of experiment
 */
const alternateProviderData = {
  ...providerData,
  benefits: {
    ...providerData.benefits,
    accessToMedication: { gkv: [], pkv: [] }
  }
};

const faqs = [
  'Sie werden im ersten Schritt zur Online-Arztpraxis weitergeleitet.',
  'Beantworten Sie im Rahmen einer digitalen Anamnese wichtige Fragen zu Ihrer Gesundheit.',
  'Die Ärzte bearbeiten Ihre Anfrage, empfehlen Ihnen nächste Schritte und stellen - falls medizinisch sinnvoll - ein Privatrezept aus. Im Falle von offenen Rückfragen kontaktieren die Ärzte Sie direkt.',
  'Bekommen Sie Ihr Medikament in 1-2 Werktagen von SHOP APOTHEKE versandkostenfrei<sup>8</sup> geliefert oder holen Sie es in einer Apotheke vor Ort ab.'
];

const highlightedTreatmentNames = ['Akne', 'Asthma', 'Erektionsstörung', 'Verhütungsmittel', 'Vorzeitiger Samenerguss'];

const handleRedirectPath = indication => {
  const treatments = useTreatments();

  const result = treatments.filter(treatment => treatment.text === indication)[0];
  const target = `${process.env.REACT_APP_PROVIDER_ZAVA_HREF}${
    result?.href
      ? result.href
      : '/online-hautcheck-spl.html?utm_medium=web&utm_source=shop-apotheke-europe&utm_term=async&utm_content=derma'
  }`;

  return `https://${target}`;
};

let TRANSIT_TIMEOUT;

const ProvidersPage = (): React.ReactElement => {
  const changeProviderPageWording = useFeature(Features.providerPageWording);

  const [showError, setShowError] = useState(false);
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [confirmIsOpen, setConfirmIsOpen] = useState(false);

  const { prescriptionType, setIndication, indication, setIndicationPrice, setPrescriptionType } = usePrescriptionStore(
    ({ prescriptionType, indication, setIndication, setIndicationPrice, setPrescriptionType }) => ({
      prescriptionType,
      indication,
      setIndication,
      setIndicationPrice,
      setPrescriptionType
    })
  );
  useInsuranceTypeParam(prescriptionType, setPrescriptionType);
  const benefits = access_to_medication[prescriptionType || 'unknown'];
  const treatments = useTreatments();
  const { triggerDatalayerEvent, triggerTrackingEvent, experiments } = useContext(AppConfigContext);
  const [activatedExperiments, setActivatedExperiments] = useState<Record<string, boolean>>({});

  const onChangeIndication = item => {
    // TODO Marko
    // In future, when we have backend service for this
    // we would want to change the structure of treatment.json and provider.zava.json
    // in a way that we end up having prices and treatments inside of the same structure
    // for now, just add the price value
    const selectedIndication = treatments.find(treatment => treatment.text === item);

    const isTop5Indications = highlightedTreatmentNames.includes(item);
    if (selectedIndication?.price && prescriptionType === InsuranceType.PUBLIC) {
      setIndicationPrice(selectedIndication.price);
    }

    if (isTop5Indications) {
      triggerTrackingEvent?.('ods-client-click-on-top-five-indications');
    }

    setIndication(item);
    setShowError(false);
  };

  const redirectUrl = handleRedirectPath(indication);

  useEffect(() => {
    if (window.location.hash.indexOf('transit') == -1) {
      setShowTransitionModal(false);
      clearTimeout(TRANSIT_TIMEOUT);
    }
  }, [window.location.hash]);

  const transitionHandle = e => {
    e.preventDefault();
    setShowTransitionModal(true);
    window.location.href = ensureHash('#transit');
    TRANSIT_TIMEOUT = setTimeout(() => {
      triggerDatalayerEvent?.('zavaTransitionLoaded');
      triggerTrackingEvent?.('ods-client-transit-to-provider');
      location.href = redirectUrl;
    }, 4 * 1000);

    triggerTrackingEvent?.('ods-client-click-on-choose-provider-button');
  };

  const isQuestionnaireVisualizationFeatureActive = useFeature(Features.questionnaireVisualization);

  const child1: React.ReactElement = (
    <>
      <Box pb={2.5} letterSpacing={0.7}>
        <Feature featureName={Features.providerPageWording} renderIfDisabled>
          <Typography variant="subtitle1">1. Wofür benötigen Sie ein Rezept?</Typography>
        </Feature>
        <Feature featureName={Features.providerPageWording}>
          <Typography variant="subtitle1">Wofür benötigen Sie ein Rezept?</Typography>
        </Feature>
      </Box>

      <BasicSelect
        id={'selectProvider'}
        error={showError}
        errorText={'Bitte wählen Sie eine Art der Beschwerde aus'}
        label="Wählen Sie die Art ihrer Beschwerde*"
        value={indication || ''}
        onChange={onChangeIndication}
      />

      <Modal
        open={confirmIsOpen}
        title={'Weiter ohne Art der Beschwerden?'}
        handleClose={() => {
          setConfirmIsOpen(false);
        }}
      >
        <>
          <Box pb={2.5} letterSpacing={0.7}>
            <Typography variant="body1">
              Wenn Sie eine Beschwerde auswählen, können wir Ihnen besser helfen. Sie können auch ohne Auswahl
              fortfahren.
            </Typography>
          </Box>
          <Box pb={2.5} letterSpacing={0.7}>
            <BasicSelect
              id={'selectProvider'}
              error={showError}
              errorText={'Bitte wählen Sie eine Art der Beschwerde aus'}
              label="Wählen Sie die Art ihrer Beschwerde"
              value={indication || ''}
              onChange={onChangeIndication}
            />
          </Box>
          <Box pt={2} pb={2.5} style={{ position: 'relative', height: '100%' }}>
            <Button
              color="primary"
              variant="contained"
              id="accesstomedication_provider_zava"
              fullWidth
              redirect={true}
              onClick={() => {
                triggerTrackingEvent?.('ods-client-click-on-choose-provider-button');
              }}
              href={redirectUrl}
            >
              Zum Anbieter
            </Button>
          </Box>
        </>
      </Modal>
      <TransitionModal open={showTransitionModal} />

      <Box pt={3.7} pb={2.5} letterSpacing={0.7}>
        <Feature featureName={Features.providerPageWording}>
          <Typography variant="subtitle1">Ihre Online Arztpraxis ZAVA</Typography>
        </Feature>
      </Box>
      <Box pt={2} style={{ position: 'relative', height: '100%' }}>
        <ProviderTile
          provider={changeProviderPageWording ? alternateProviderData : providerData}
          area={Area.ACCESS_TO_MEDICATION}
          insuranceType={prescriptionType}
        >
          <Box>
            <Button
              data-qa-id={'accesstomedication-provider-zava'}
              color="primary"
              variant="contained"
              id="accesstomedication_provider_zava"
              fullWidth
              redirect={true}
              onClick={e => {
                transitionHandle(e);
              }}
              href={redirectUrl}
            >
              <Feature featureName={Features.providerPageWording} renderIfDisabled>
                Anbieter wählen
              </Feature>
              <Feature featureName={Features.providerPageWording}>Jetzt Online Rezept anfragen</Feature>
            </Button>
          </Box>
          <Feature featureName={Features.providerPageWording}>
            <Box pt={2.5}>
              <Typography color={'textSecondary'} align="center">
                Sie werden jetzt zu Zava weitergeleitet
              </Typography>
            </Box>
          </Feature>
        </ProviderTile>
      </Box>
    </>
  );

  const child2: React.ReactElement = (
    <>
      {isQuestionnaireVisualizationFeatureActive ? (
        <VisualizationBox title="So funktioniert's" items={VisualizationBoxItems} />
      ) : (
        <ListBox title="Benötigen Sie Hilfe?" subtitle="So funktioniert's" elements={faqs} />
      )}

      <Box pt={3.7} pb={2.5} letterSpacing={0.7}>
        <PrimaryAlert
          showIcon
          text="*Mit dem Klick auf den Button werden Sie an ZAVA (Health Bridge Limited Medical, 2 Dublin Landings, N Wall
            Quay, North Dock Dublin, D01 V4A3, Irland) weitergeleitet. ZAVA ist einer von mehreren Telemedizinanbietern
            mit Sitz in Irland und einer der führenden Anbieter telemedizinischer Leistungen für Patienten in Deutschland
            und Europa. Die Ärzte sind deutschsprachig und bei der irischen Ärztekammer (Medical Council of the Republic
            of Ireland) registriert."
        />
      </Box>
    </>
  );

  return <OdsLayout children1={child1} children2={child2} />;
};

export { ProvidersPage };
